import { Metavalues, Result, TransportContext, TransportSessionBuilder } from "@peercolab/engine";
import { CreateManyTranslationKeys, CreateTranslationKey, DeleteTranslationKey, GetLanguageFiles, GetLanguages, Language, LanguageValue, PatchManyTranslations, PatchTranslation, PublishAllChanges, PublishChanges, RevertAllChanges, RevertChanges, SearchTranslationKeys, SearchTranslationKeysOutputKeys } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Translations/PathItems";
import { GetLanguageFilesOutput, SearchTranslationKeysOutput } from "../../PetKey/Main/Model/1_0_Initial/PathItems";

type DbTranslationValue = {
  languageId: string;
  value: string;
}

type DbTranslation = {
  key: string;
  values: DbTranslationValue[];
  modifiedAt: Date
  state: string
  publishedValues: DbTranslationValue[]
  lastPublishedAt?: Date
}

export type TranslationAppender = (category: string, name: string, contents?: string) => string

export function mockTranslationInterceptors(builder: TransportSessionBuilder): TranslationAppender {
  const dbLanguages: Language[] = [];
  dbLanguages.push({ id: 'en', name: 'English', }, { id: 'no', name: 'Norwegian', })
  const dbTranslations: DbTranslation[] = [];

  const getTranslationKey = (key: string, ctx: TransportContext): Result<DbTranslation> => {
    const translation = dbTranslations.find(s => s.key === key);
    if (!translation) {
      return Result.notFound('Backoffice.Translation.TranslationKeyNotFound', `Translation key with id ${key} not found`);
    }
    return Result.ok(translation);
  };

  const getTranslationKeys = (keys: string[], ctx: TransportContext): Result<DbTranslation[]> => {
    const translations = dbTranslations.filter(s => keys.includes(s.key));
    if (translations.length !== keys.length) {
      return Result.notFound('Backoffice.Translation.TranslationKeyNotFound', `Translation keys with ids ${keys.join(', ')} not found`);
    }
    return Result.ok(translations);
  };

  const saveTranslationKey = (result: Result<DbTranslation>, ctx: TransportContext): Result<DbTranslation> => {
    if (!result.success) {
      return result;
    }
    const translation = result.value;
    translation.modifiedAt = new Date();
    const existingIndex = dbTranslations.findIndex(s => s.key === translation.key);
    if (existingIndex !== -1) {
      dbTranslations[existingIndex] = translation;
    } else {
      dbTranslations.push(translation);
    }
    return Result.ok(translation);
  };

  const saveManyTranslationKeys = (result: Result<DbTranslation[]>, ctx: TransportContext): Result<DbTranslation[]> => {
    if (!result.success) {
      return result;
    }
    const output: DbTranslation[] = [];
    for (const translation of result.value) {
      translation.modifiedAt = new Date();
      const existingIndex = dbTranslations.findIndex(s => s.key === translation.key);
      if (existingIndex !== -1) {
        dbTranslations[existingIndex] = translation;
      } else {
        dbTranslations.push(translation);
      }
      output.push(translation);
    }
    return Result.ok(output);
  };

  const createTranslationKey = (key: string, lang1: string, lang2: string, value1: string, value2: string) => {
    const newKey: DbTranslation = {
        key: key,
        values: [],
        modifiedAt: new Date(),
        state: 'draft',
        publishedValues: [],
      }
      if (value1.trim().length > 0) {
        newKey.values.push({
          languageId: lang1,
          value: value1,
        });
      }
      if (value2.trim().length > 0) {
        newKey.values.push({
          languageId: lang2,
          value: value2,
        });
      }
      return newKey
  }

  builder
    .intercept(new GetLanguages().handle(async (input, ctx) => {
      return Result.ok(dbLanguages);
    }))
    .intercept(new SearchTranslationKeys().handle(async (input, ctx) => {
      const allFiltered = [...dbTranslations]
        .sort((a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime())
        .filter(s => {
          const value1 = s.values.find((v: DbTranslationValue) => v.languageId === input.language1Id);
          const value2 = s.values.find((v: DbTranslationValue) => v.languageId === input.language2Id);
          if (input.onlyMissingForLang2 && (value2?.value ?? '').trim().length !== 0)
            return false;
          if (input.stateType) {
            if (input.stateType === 'published' && !s.lastPublishedAt)
              return false;
            const value1 = s.values.find(v => v.languageId === input.language1Id)
            const value2 = s.values.find(v => v.languageId === input.language2Id)
            const publishedValue1 = s.publishedValues.find(v => v.languageId === input.language1Id)?.value
            const publishedValue2 = s.publishedValues.find(v => v.languageId === input.language2Id)?.value
            const value1HasChanges = (value1?.value ?? '') !== (publishedValue1 ?? '')
            const value2HasChanges = (value2?.value ?? '') !== (publishedValue2 ?? '')
            const hasChanges = value1HasChanges || value2HasChanges
            if (input.stateType === 'draft' && !hasChanges)
              return false;
            else if (input.stateType === 'published' && (s.state !== 'published' || hasChanges))
              return false;
          }

          if (input.searchType === 'all') {
            return s.key.toLowerCase().includes(input.searchInput.query.toLowerCase()) ||
              (value1 && value1.value.toLowerCase().includes(input.searchInput.query.toLowerCase())) ||
              (value2 && value2.value.toLowerCase().includes(input.searchInput.query.toLowerCase()));
          } else if (input.searchType === 'keyonly') {
            return s.key.toLowerCase().includes(input.searchInput.query.toLowerCase());
          } else if (input.searchType === 'keyandlang1') {
            return s.key.toLowerCase().includes(input.searchInput.query.toLowerCase()) ||
              (value1 && value1.value.toLowerCase().includes(input.searchInput.query.toLowerCase()));
          } else if (input.searchType === 'keyandlang2') {
            return s.key.toLowerCase().includes(input.searchInput.query.toLowerCase()) ||
              (value2 && value2.value.toLowerCase().includes(input.searchInput.query.toLowerCase()));
          } else {
            return false;
          }
        });
      const filtered = allFiltered
        .slice(input.searchInput.offset, input.searchInput.offset + input.searchInput.limit)
        .map(s => {
          const value1 = s.values.find(v => v.languageId === input.language1Id)
          const value2 = s.values.find(v => v.languageId === input.language2Id)
          const publishedValue1 = s.publishedValues.find(v => v.languageId === input.language1Id)?.value
          const publishedValue2 = s.publishedValues.find(v => v.languageId === input.language2Id)?.value
          const value1HasChanges = (value1?.value ?? '') !== (publishedValue1 ?? '')
          const value2HasChanges = (value2?.value ?? '') !== (publishedValue2 ?? '')
          const hasChangesSincePublish = s.lastPublishedAt && (value1HasChanges || value2HasChanges)
          const keys: SearchTranslationKeysOutputKeys = {
            key: s.key,
            value1: value1?.value ?? '',
            value2: value2?.value ?? '',
            stateType: hasChangesSincePublish ? 'draft' : s.state,
            lastPublishedAt: s.lastPublishedAt,
            publishedValue1,
            publishedValue2,
          }
          return keys
        });
      const output: SearchTranslationKeysOutput = {
        language1: input.language1Id,
        language2: input.language2Id,
        keys: filtered
      }
      const meta = new Metavalues();
      if (allFiltered.length > input.searchInput.offset + input.searchInput.limit) {
        meta.setHasMoreValues();
      }
      return Result.ok(output, meta);
    }))
    .intercept(new CreateTranslationKey().handle(async (input, ctx) => {
      return saveTranslationKey(Result.ok(createTranslationKey(
        input.key,
        input.language1Value.languageId,
        input.language2Value.languageId,
        input.language1Value.value,
        input.language2Value.value
      )), ctx).convertToEmpty();
    }))
    .intercept(new CreateManyTranslationKeys().handle(async (input, ctx) => {
      return saveManyTranslationKeys(Result.ok(input.keys.map(key => createTranslationKey(
        key.key,
        input.language1,
        input.language2,
        key.value1,
        key.value2
      ))), ctx).convertToEmpty();
    }))
    .intercept(new PatchTranslation().handle(async (input, ctx) => {
      return saveTranslationKey(getTranslationKey(input.key, ctx)
        .maybe((translation, meta) => {
          patchTranslation(translation, input.language1Value);
          patchTranslation(translation, input.language2Value);
          return Result.ok(translation, meta);
        }), ctx).convertToEmpty()
    }))
    .intercept(new PatchManyTranslations().handle(async (input, ctx) => {
      return saveManyTranslationKeys(Result.ok(input.keys.map(key => {
        const result = getTranslationKey(key.key, ctx)
        if (!result.success) return result;
        patchTranslation(result.value, { languageId: input.language1, value: key.value1 });
        patchTranslation(result.value, { languageId: input.language2, value: key.value2 });
        return Result.ok(result.value);
      })).maybe((translations, meta) => {
        const mapped: DbTranslation[] = []
        for (const t of translations) {
          if (!t.success) {
            return t.convert<DbTranslation[]>();
          }
          mapped.push(t.value);
        }
        return Result.ok(mapped, meta);
      }), ctx).convertToEmpty();
    }))
    .intercept(new DeleteTranslationKey().handle(async (key, ctx) => {
      const item = dbTranslations.find(t => t.key === key);
      if (item)
        dbTranslations.splice(dbTranslations.indexOf(item), 1);
      return Result.ok();
    }))
    .intercept(new PublishChanges().handle(async (input, ctx) => {
      return saveManyTranslationKeys(getTranslationKeys(input.keys, ctx)
        .maybe((keys, meta) => {
          for (const key of keys) {
            const publishedValues = key.publishedValues;
            for (const v of key.values) {
              if (input.languages) {
                if (!input.languages.includes(v.languageId)) {
                  continue;
                }
                const currentPublished = key.publishedValues.find(pv => pv.languageId === v.languageId);
                if (currentPublished) {
                  currentPublished.value = v.value;
                } else {
                  key.publishedValues.push({ languageId: v.languageId, value: v.value });
                }
              }
            }
            key.publishedValues = publishedValues;
            key.lastPublishedAt = new Date();
            key.state = 'published';
          }
          return Result.ok(keys, meta);
        }), ctx).convertToEmpty();
    }))
    .intercept(new RevertChanges().handle(async (input, ctx) => {
      return saveManyTranslationKeys(getTranslationKeys(input.keys, ctx)
        .maybe((keys, meta) => {
          for (const key of keys) {
            for (const v of key.values) {
              if (input.languages) {
                if (!input.languages.includes(v.languageId)) {
                  continue;
                }
                const currentPublished = key.publishedValues.find(pv => pv.languageId === v.languageId);
                if (currentPublished) {
                  v.value = currentPublished.value;
                } else {
                  v.value = '';
                }
              }
            }
            key.lastPublishedAt = new Date();
            key.state = 'published';
          }
          return Result.ok(keys, meta);
        }), ctx).convertToEmpty();
    }))
    .intercept(new PublishAllChanges().handle(async (input, ctx) => {
      return saveManyTranslationKeys(Result.ok(dbTranslations)
        .maybe((keys, meta) => {
          for (const key of keys) {
            const publishedValues = key.publishedValues;
            for (const v of key.values) {
              if (input.languages) {
                if (!input.languages.includes(v.languageId)) {
                  continue;
                }
                const currentPublished = key.publishedValues.find(pv => pv.languageId === v.languageId);
                if (currentPublished) {
                  currentPublished.value = v.value;
                } else {
                  key.publishedValues.push({ languageId: v.languageId, value: v.value });
                }
              }
            }
            key.publishedValues = publishedValues;
            key.lastPublishedAt = new Date();
            key.state = 'published';
          }
          return Result.ok(keys, meta);
        }), ctx).convertToEmpty();
    }))
    .intercept(new RevertAllChanges().handle(async (input, ctx) => {
      return saveManyTranslationKeys(Result.ok(dbTranslations)
        .maybe((keys, meta) => {
          for (const key of keys) {
            for (const v of key.values) {
              if (input.languages) {
                if (!input.languages.includes(v.languageId)) {
                  continue;
                }
                const currentPublished = key.publishedValues.find(pv => pv.languageId === v.languageId);
                if (currentPublished) {
                  v.value = currentPublished.value;
                } else {
                  v.value = '';
                }
              }
            }
            key.lastPublishedAt = new Date();
            key.state = 'published';
          }
          return Result.ok(keys, meta);
        }), ctx).convertToEmpty();
    }))
    .intercept(new GetLanguageFiles().handle(async (input, ctx) => {
      const files: GetLanguageFilesOutput[] = []
      for (const langId of input.languages) {
        const lang: GetLanguageFilesOutput  = { language: langId, jsonContents: '{"test": { "language": "File" } }' }
        files.push(lang)
      }
      return Result.ok(files);
    }))

    return (category, name, contents) => {
      const nameBase = 'backoffice.' + category + '.'
      const camelCaseName = toCamelCase(name)
      let key = nameBase + camelCaseName
      let increment = 1
      while (true) {
        const currentKey = key; // capture key value for this iteration
        const existing = dbTranslations.find(t => t.key === currentKey)
        if (!existing)
          break
        increment++
        key = nameBase + camelCaseName + increment.toString()
      }
      dbTranslations.push({
        key: key,
        values: [{ languageId: 'en', value: contents ?? name }],
        modifiedAt: new Date(),
        state: 'draft',
        publishedValues: [],
      })
      return key
    }
}

function toCamelCase(str: string) {
  // Remove anything except for letters, numbers and spaces
  const onlyLettersNumbersAndSpaces = str.replace(/[^a-zA-Z0-9\s]/g, '');
  const words = onlyLettersNumbersAndSpaces.trim().split(/\s+/);
  if (words.length === 0) return '';
  return words
    .map((word, idx) =>
      idx === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');
}

function patchTranslation(translation: DbTranslation, newValue: LanguageValue) {
  const currentValue = translation.values.find(v => v.languageId === newValue.languageId)
  if (currentValue && newValue.value.trim().length === 0) {
    translation.values.splice(translation.values.indexOf(currentValue), 1);
  } else if (currentValue) {
    currentValue.value = newValue.value;
  } else if (newValue.value.trim().length > 0) {
    translation.values.push({
      languageId: newValue.languageId,
      value: newValue.value,
    });
  }
}
